import variables from "@/common/globalVariables";
import React from "react";

class AutoCompleteInputField extends React.PureComponent {
  _timeoutID;
  _lastChange = null;

  state = {
    value: this.props.value || "",
    optionsToDisplay: null,
    options: [],
    hasValueFromOptions: false,
    showMenu: false,
    isManagingFocus: false,
    valid: !!this.props.value,
    errors: [],
    name: this.props.name || "",
    touched: false,
    helpLabel: "Please type something",
  };

  componentDidUpdate(prevProps, prevState) {
    if (this.props.validate !== prevProps.validate && this.props.validate) {
      this.validate();
    }
  }

  sameChangePayload = (a, b) => {
    if (!a || !b) return false;
    if (
      a.value !== b.value ||
      a.valid !== b.valid ||
      a.touched !== b.touched ||
      a.errors.length !== b.errors.length
    ) {
      return false;
    }
    return a.errors.every((item, index) => item === b.errors[index]);
  };

  notifyChange = (next) => {
    if (!this.props.change) return;
    const payload = {
      value: next.value,
      valid: !!next.valid,
      touched: !!next.touched,
      errors: Array.isArray(next.errors) ? next.errors : [],
    };
    if (this.sameChangePayload(this._lastChange, payload)) return;
    this._lastChange = payload;
    this.props.change(payload);
  };

  inputTextChangeHandler = (e) => {
    this.updateField(e.target.value);

    if (this.props.ajax && e.target.value !== "") {
      this.props.callback(e.target.value);
    }
  };

  handleStateChange = (state) => {
    this.setState((prev) => ({ ...prev, ...state }), () => {
      this.notifyChange(this.state);
    });
  };

  updateField = (value) => {
    const hasValueFromOptions = this.fieldIsValid(value);
    const next = { value, hasValueFromOptions, showMenu: true };
    if (!this.props.ajax) {
      next.options = filterOptions(value, this.props.options);
    }
    if (!hasValueFromOptions && this.props.required) {
      next.valid = false;
      next.errors = ["Please select an option from the dropdown"];
      next.options = [];
    }
    this.handleStateChange(next);
  };

  fieldIsValid = (value) => {
    if (!value || !Array.isArray(this.props.options)) return false;
    const normalized = value.trim().toLowerCase();
    return this.props.options.some(
      (option) => option.value.trim().toLowerCase() === normalized
    );
  };

  validate = () => {
    if (
      !this.props.required ||
      (this.props.value && this.props.value !== "" && !this.state.touched)
    ) {
      this.handleStateChange({
        hasValueFromOptions: false,
        errors: [],
        options: [],
        valid: true,
        touched: true,
      });
    } else if (this.props.required && !this.state.hasValueFromOptions) {
      this.handleStateChange({
        hasValueFromOptions: false,
        errors: ["Please select an option from the dropdown"],
        options: [],
        valid: false,
        touched: true,
      });
    }
  };

  handleOptionClick = (e, value) => {
    e.preventDefault();
    e.stopPropagation();

    this.handleStateChange({
      showMenu: false,
      value: value,
      hasValueFromOptions: true,
      valid: true,
      errors: [],
      touched: true,
    });
  };

  _onBlur = (e) => {
    e.preventDefault();
    // e.stopPropagation();

    this._timeoutID = setTimeout(() => {
      if (this.state.isManagingFocus) {
        this.handleStateChange({
          isManagingFocus: false,
          showMenu: false,
          touched: true,
        });
      }

      if (!this.state.hasValueFromOptions && this.props.required) {
        this.handleStateChange({
          errors: ["Please select an option from the dropdown"],
          options: [],
        });
      }
    }, 0);
  };

  _onFocus = () => {
    clearTimeout(this._timeoutID);
    if (!this.state.isManagingFocus) {
      this.handleStateChange({
        isManagingFocus: true,
        showMenu: true,
      });
    }
  };

  render() {
    let options = this.props.ajax ? this.props.options : this.state.options;

    if (this.state.textFieldValue === "") {
      options = [];
    }

    const optionsElements = options.map((option, index) => {
      return (
        <div
          key={index}
          className={"Option"}
          onMouseDown={(e) => this.handleOptionClick(e, option.value)}
        >
          {option.label}
        </div>
      );
    });

    return (
      <div
        // onBlur={this.handleBlur}
        className={`AutoCompleteInputField${
          this.state.showMenu ? " is-open" : ""
        }`}
        onFocus={this._onFocus}
        onBlur={this._onBlur}
      >
        <input
          type="text"
          placeholder={this.props.placeholder}
          value={this.state.value}
          onChange={this.inputTextChangeHandler}
        />
        {this.props.loading && (
          <span className="LocationSpinner" aria-hidden="true" />
        )}
        {this.state.showMenu && (
          <div className={"Options"}>
            {this.props.loading && options.length === 0 ? (
              <div className="Option OptionStatus">Searching locations...</div>
            ) : options.length > 0 ? (
              optionsElements
            ) : (
              <div className="Option">Nothing found</div>
            )}
          </div>
        )}

        <style jsx>{`
          .AutoCompleteInputField {
            width: 100%;
          }

          input,
          textarea,
          select {
            border: none;
            margin: 5px 20px 5px 15px;
            width: 90%;
            outline: none;
          }

          .LocationSpinner {
            position: absolute;
            right: 12px;
            top: 50%;
            width: 16px;
            height: 16px;
            margin-top: -8px;
            border-radius: 50%;
            border: 2px solid ${variables.mutedColor2};
            border-top-color: ${variables.accentColor1};
            animation: locationSpin 0.7s linear infinite;
          }

          @keyframes locationSpin {
            to {
              transform: rotate(360deg);
            }
          }

          input::placeholder,
          textarea::placeholder,
          select::placeholder {
            color: ${variables.secondaryTextColor};
          }

          textarea {
            min-height: 300px;
            padding-top: 15px;
          }

          label {
            color: ${variables.baseTextColor};
          }

          .Options {
            left: 0;
            right: 0;
            background-color: ${variables.lightColor};
            border: 1px solid ${variables.mutedColor2};
            border-top: none;
            position: absolute;
            top: 100%;
            margin-top: -1px;
            z-index: 999;
            border-radius: 0 0 16px 16px;
            padding: 4px 0 6px;
            box-shadow: 0 10px 18px -10px rgba(0, 0, 0, 0.35);
            cursor: pointer;
          }

          .Options :global(.Option) {
            margin-bottom: 5px;
            padding: 10px;
            transition: 200ms;
          }

          .Options :global(.Option):hover {
            background-color: ${variables.accentColor3};
            color: ${variables.lightColor};
          }

          :global(.InputContainer:has(.is-open)) {
            border-bottom-left-radius: 0 !important;
            border-bottom-right-radius: 0 !important;
            z-index: 20;
          }
        `}</style>
      </div>
    );
  }
}

const filterOptions = (sentence, options = []) => {
  const words = sentence
    .replace(/[^a-zA-Z0-9\s]/g, "")
    .trim()
    .split(" ");
  let result = [];
  let filteredWords = [];
  words.forEach((word) => {
    if (word.length > 0) {
      options.forEach((option) => {
        if (
          option.value.toLowerCase().includes(word.toLowerCase()) &&
          !filteredWords.includes(option.value.toLowerCase())
        ) {
          result.push(option);
          filteredWords.push(option.value.toLowerCase());
        }
      });
    }
  });
  return sentence === "" ? options : result;
};

// const comparisonFn = function(prevProps, nextProps) {

//   return prevProps.options === nextProps.options;
// };

export default AutoCompleteInputField;
